import type { Client, RequestContext } from "../client";
import type { GitRef, DeleteProjectTemplateCommand, ModifyProjectTemplateCommand, NewProjectTemplate, ProjectTemplateListSummaryResource, ProjectTemplateSummary, ProjectTemplateResource, GitLinkResult, ProjectTemplateVersionResource, GetSharedSpacesToProjectTemplateBySlugResponse, PublishProjectTemplateCommand, PublishProjectTemplateResponse, ShareProjectTemplateToManySpacesCommand, ShareProjectTemplateToManySpacesResponse, ProjectTemplateProcessResource, GetProjectTemplateParameterSetRequest, ProjectTemplateParameterSetResource, ModifyProjectTemplateParameterSetCommand, ProjectTemplateVariableSetResource, ModifyProjectTemplateVariableSetCommand, ModifyProjectTemplateSensitiveVariableSetCommand, ProjectTemplateStatusResponse, } from "../resources";
export class ProjectTemplatesRepository {
    protected readonly client: Client;
    protected readonly context: RequestContext;
    constructor(client: Client, requestContext: RequestContext) {
        this.client = client;
        this.context = requestContext;
    }
    summaries() {
        return this.client.get<ProjectTemplateListSummaryResource[]>(this.context, "~/api/platformhub/projecttemplates/summaries");
    }
    get(slug: string, gitRef: string | undefined) {
        return this.client.get<ProjectTemplateResource>(this.context, "~/api/platformhub/{gitRef}/projecttemplates/{slug}", { slug, gitRef });
    }
    del(projectTemplate: DeleteProjectTemplateCommand) {
        return this.client.post(this.context, "~/api/platformhub/projecttemplates/delete-by-id", projectTemplate);
    }
    add(projectTemplate: NewProjectTemplate, gitRef: GitRef) {
        return this.client.post<ProjectTemplateResource>(this.context, "~/api/platformhub/{gitRef}/projecttemplates", projectTemplate, { gitRef });
    }
    modify(projectTemplate: ModifyProjectTemplateCommand) {
        return this.client.put<ProjectTemplateResource>(this.context, "~/api/platformhub/projecttemplates/{slug}", projectTemplate, { slug: projectTemplate.Id });
    }
    getPaginatedTemplates(projectTemplatesPageFilter: GetProjectTemplatesBffRequest): Promise<GetProjectTemplatesBffResponse> {
        return this.client.get<GetProjectTemplatesBffResponse>(this.context, "~/api/platformhub/{gitRef}/projecttemplates{?skip,take,partialName}", projectTemplatesPageFilter);
    }
    shareToSpace(shareCommand: ShareProjectTemplateToManySpacesCommand) {
        return this.client.post<ShareProjectTemplateToManySpacesResponse>(this.context, "~/api/platformhub/{gitRef}/projecttemplates/{slug}/share", shareCommand, {
            slug: shareCommand.ProjectTemplateSlug,
            gitRef: shareCommand.GitRef,
        });
    }
    getSharedSpaces(slug: string) {
        return this.client.get<GetSharedSpacesToProjectTemplateBySlugResponse>(this.context, "~/api/platformhub/projecttemplates/{slug}/share", { slug });
    }
    getVersions(slug: string, skip: number = 0, take: number = 100) {
        return this.client.get<{
            ProjectTemplateVersions: ProjectTemplateVersionResource[];
        }>(this.context, "~/api/platformhub/projecttemplates/{slug}/versions{?skip,take}", { slug, skip, take });
    }
    getLink(slug: string, gitRef: GitRef) {
        return this.client.get<GitLinkResult>(this.context, "~/api/platformhub/{gitRef}/projecttemplates/{slug}/link", { slug, gitRef });
    }
    publishVersion(publishCommand: PublishProjectTemplateCommand) {
        return this.client.post<PublishProjectTemplateResponse>(this.context, "~/api/platformhub/{gitRef}/projecttemplates/{slug}/publish", publishCommand, {
            slug: publishCommand.ProjectTemplateSlug,
            gitRef: publishCommand.GitRef,
        });
    }
    getDeploymentProcess(slug: string, gitRef: string | undefined) {
        return this.client.get<ProjectTemplateProcessResource>(this.context, "~/api/platformhub/{gitRef}/projecttemplates/{slug}/deploymentprocess", { slug, gitRef });
    }
    modifyDeploymentProcess(deploymentProcess: ProjectTemplateProcessResource, slug: string, gitRef: string | undefined) {
        return this.client.put<ProjectTemplateProcessResource>(this.context, "~/api/platformhub/{gitRef}/projecttemplates/{slug}/deploymentprocess", deploymentProcess, { slug, gitRef });
    }
    getParameterSet(getRequest: GetProjectTemplateParameterSetRequest) {
        return this.client.get<ProjectTemplateParameterSetResource>(this.context, "~/api/platformhub/{gitRef}/projecttemplates/{slug}/parameters", {
            slug: getRequest.ProjectTemplateSlug,
            gitRef: getRequest.GitRef,
        });
    }
    modifyParameterSet(modifyCommand: ModifyProjectTemplateParameterSetCommand) {
        return this.client.put<ModifyProjectTemplateParameterSetCommand>(this.context, "~/api/platformhub/{gitRef}/projecttemplates/{slug}/parameters", modifyCommand, {
            slug: modifyCommand.ProjectTemplateSlug,
            gitRef: modifyCommand.GitRef,
        });
    }
    getVariableSet(getRequest: {
        GitRef: string;
        ProjectTemplateSlug: string;
    }) {
        return this.client.get<ProjectTemplateVariableSetResource>(this.context, "~/api/platformhub/{gitRef}/projecttemplates/{slug}/variables", {
            slug: getRequest.ProjectTemplateSlug,
            gitRef: getRequest.GitRef,
        });
    }
    modifyVariableSet(modifyCommand: ModifyProjectTemplateVariableSetCommand) {
        return this.client.put<ProjectTemplateVariableSetResource>(this.context, "~/api/platformhub/{gitRef}/projecttemplates/{slug}/variables", modifyCommand, {
            slug: modifyCommand.ProjectTemplateSlug,
            gitRef: modifyCommand.GitRef,
        });
    }
    getVariableSetDownloadUrl(slug: string, gitRef: string): string {
        return this.client.resolve("~/api/platformhub/{gitRef}/projecttemplates/{slug}/variables", { slug, gitRef });
    }
    getSensitiveVariableSet(slug: string) {
        return this.client.get<ProjectTemplateVariableSetResource>(this.context, "~/api/platformhub/projecttemplates/{slug}/variables/sensitive", { slug });
    }
    modifySensitiveVariableSet(modifyCommand: ModifyProjectTemplateSensitiveVariableSetCommand) {
        return this.client.put<ProjectTemplateVariableSetResource>(this.context, "~/api/platformhub/projecttemplates/{slug}/variables/sensitive", modifyCommand, {
            slug: modifyCommand.ProjectTemplateSlug,
        });
    }
    getStatus(slug: string, gitRef: string): Promise<ProjectTemplateStatusResponse> {
        return this.client.get<ProjectTemplateStatusResponse>(this.context, "~/api/platformhub/{gitRef}/projecttemplates/{slug}/status", {
            slug,
            gitRef,
        });
    }
}
export type GetProjectTemplatesBffRequest = {
    skip: number;
    take: number;
    partialName?: string;
    gitRef: string;
};
export interface GetProjectTemplatesBffResponse {
    ProjectTemplates: ProjectTemplateSummary[];
    TotalResults: number;
    ItemsPerPage: number;
    TotalNoOfProjectTemplates: number;
}
